import { api } from "./client";

export interface User {
  id: string;
  _id: string;
  email: string;
  name: string;
  role: "admin" | "analyst" | "client" | "super_admin";
  status?: "active" | "suspended" | "pending";
  is_active: boolean;
  created_at: string;
  first_access_at?: string | null;
  last_login: string | null;
  industry?: string;
  sector?: string;
  plan: string;
  users_count: number;
  assets_count: number;
  is_protected?: boolean;
  permission_overrides?: Record<string, boolean>;
  has_custom_overrides?: boolean;
}

export interface CreateUserPayload {
  email: string;
  name: string;
  password?: string;
  role?: string;
  industry?: string;
  plan?: string;
  status?: string;
  is_active?: boolean;
  permission_overrides?: Record<string, boolean>;
  send_invite?: boolean;
}

export interface GetUsersParams {
  search?: string;
  role?: string;
  status?: string;
  sector?: string;
  plan?: string;
  skip?: number;
  limit?: number;
  sort?: string;
  direction?: string;
}

export async function getUsers(
  tokenOrParams?: string | GetUsersParams,
  params?: GetUsersParams
) {
  const finalToken = typeof tokenOrParams === "string" ? tokenOrParams : (localStorage.getItem("access_token") || null);
  const finalParams = typeof tokenOrParams === "object" ? tokenOrParams : params;

  // Clean params so "all" / "All Roles" literal strings are excluded from API query
  const cleanParams: Record<string, any> = {};
  if (finalParams) {
    if (finalParams.search && finalParams.search.trim() !== "") cleanParams.search = finalParams.search.trim();
    if (finalParams.role && !["all", "all roles"].includes(finalParams.role.toLowerCase())) {
      cleanParams.role = finalParams.role;
    }
    if (finalParams.status && !["all", "all statuses"].includes(finalParams.status.toLowerCase())) {
      cleanParams.status = finalParams.status;
    }
    if (finalParams.sector && !["all", "all sectors"].includes(finalParams.sector.toLowerCase())) {
      cleanParams.sector = finalParams.sector;
    }
    if (finalParams.plan && !["all", "all plans"].includes(finalParams.plan.toLowerCase())) {
      cleanParams.plan = finalParams.plan;
    }
    if (typeof finalParams.skip === "number") cleanParams.skip = finalParams.skip;
    if (typeof finalParams.limit === "number") cleanParams.limit = finalParams.limit;
    if (finalParams.sort) cleanParams.sort = finalParams.sort;
    if (finalParams.direction) cleanParams.direction = finalParams.direction;
  }

  const headers: Record<string, string> = {};
  if (finalToken) {
    headers["Authorization"] = `Bearer ${finalToken}`;
  }

  const response = await api.get("/admin/users", {
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
  });

  let rawList: any[] = [];
  let totalCount = 0;
  let kpis = response.data?.kpis;

  if (response.data) {
    if (Array.isArray(response.data.items)) {
      rawList = response.data.items;
      totalCount = response.data.total ?? rawList.length;
    } else if (Array.isArray(response.data.clients)) {
      rawList = response.data.clients;
      totalCount = response.data.total ?? rawList.length;
    } else if (Array.isArray(response.data.users)) {
      rawList = response.data.users;
      totalCount = response.data.total ?? rawList.length;
    } else if (Array.isArray(response.data.data)) {
      rawList = response.data.data;
      totalCount = response.data.total ?? rawList.length;
    } else if (Array.isArray(response.data)) {
      rawList = response.data;
      totalCount = rawList.length;
    }
  }

  // Safe item normalization with fallbacks
  const items = rawList.map((u: any) => {
    const roleStr = (u.role || u.user_role || "client").toString().toLowerCase();
    const isActive = u.is_active ?? u.active ?? (u.status ? u.status === "active" : true);
    const statusStr = u.status || (isActive ? "active" : "suspended");
    const idVal = (u.id || u._id || u.user_id || Math.random().toString()).toString();

    return {
      id: idVal,
      _id: idVal,
      email: u.email || "",
      name: u.name || u.username || u.client_name || (u.email ? u.email.split("@")[0] : "User"),
      role: roleStr,
      status: statusStr,
      is_active: isActive,
      created_at: u.created_at || u.joined_date || u.created || new Date().toISOString(),
      last_login: u.last_login || u.last_active || null,
      industry: u.industry || u.sector || u.company_sector || "",
      sector: u.sector || u.industry || u.company_sector || "",
      plan: u.plan || "Enterprise",
      users_count: typeof u.users_count === "number" ? u.users_count : (roleStr === "client" ? 1 : 0),
      assets_count: typeof u.assets_count === "number" ? u.assets_count : (roleStr === "client" ? 3 : 0),
      is_protected: Boolean(u.is_protected || u.protected || roleStr === "super_admin" || u.email === "superanalyst@threatactix.com"),
      permission_overrides: u.permission_overrides || u.custom_permissions || {},
      has_custom_overrides: Boolean(u.has_custom_overrides || (u.permission_overrides && Object.keys(u.permission_overrides).length > 0)),
    };
  });

  return {
    items,
    total: totalCount || items.length,
    page: response.data?.page ?? 1,
    pages: response.data?.pages ?? 1,
    kpis,
  };
}

export async function getPendingUsers() {
  const data = await getUsers(undefined, { limit: 100 });
  const pending = (data.items || []).filter((u: User) => !u.is_active || u.status === "pending");
  return {
    total: pending.length,
    items: pending,
  };
}

export async function createUser(token: string, payload: CreateUserPayload) {
  const response = await api.post("/admin/users", payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
}

export async function updateUserStatus(
  token: string,
  userId: string,
  isActive: boolean
) {
  const response = await api.patch(
    `/admin/users/${userId}/status`,
    { is_active: isActive, status: isActive ? "active" : "suspended" },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}
